import { useState } from "react";
import { Copy, ExternalLink, PlugZap, Unplug, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Panel, Tag, Spinner } from "@/components/common";

const STATE_TONE = { connected: "text-emerald-300", not_authorized: "text-amber-300", credentials_needed: "text-red-300" };

const openTop = (url) => {
  try {
    window.top.location.href = url;
  } catch {
    window.open(url, "_blank", "noopener");
  }
};

export default function ConnectionStatus({ status, onChange }) {
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/google-ads/oauth/start");
      openTop(data.auth_url);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete("/google-ads/connection");
      toast.success("Disconnected from Google Ads");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => { navigator.clipboard?.writeText(status.redirect_uri); toast.success("Redirect URI copied"); };

  return (
    <Panel title="Connection status" icon={PlugZap} testid="connection-status-panel"
      right={<Tag tone="cyan">API {status.api_version}</Tag>}>
      <div data-testid="connection-state-label" className={`font-display text-3xl font-bold uppercase tracking-tight ${STATE_TONE[status.state]}`}>
        {status.label}
      </div>
      {status.connected && <div className="mt-1 font-mono text-xs text-slate-500">Authorized {fmtDate(status.connected_at)}</div>}

      {status.missing.length > 0 && (
        <div className="mt-6">
          <div className="cc-eyebrow mb-3">Missing values ({status.missing.length})</div>
          <ul data-testid="missing-credentials-list" className="space-y-2">
            {status.missing.map((m) => (
              <li key={m} data-testid={`missing-${m}`} className="flex items-center gap-2 font-mono text-sm text-red-300">
                <XCircle className="h-4 w-4" /> {m}
              </li>
            ))}
          </ul>
        </div>
      )}
      {status.invalid.map((i) => (
        <div key={i.field} data-testid={`invalid-${i.field}`} className="mt-3 font-mono text-sm text-amber-300">{i.field}: {i.message}</div>
      ))}
      {status.configured && !status.has_refresh_token && (
        <div className="mt-6 flex items-center gap-2 text-sm text-slate-300"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> All credentials present. Authorize with Google to finish.</div>
      )}

      <div className="mt-6">
        <div className="cc-eyebrow mb-2">Authorized redirect URI (add this in Google Cloud Console)</div>
        <div className="flex items-center gap-2">
          <code data-testid="redirect-uri-value" className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-[#1e293b] bg-[#0a101d] px-3 py-2 font-mono text-xs text-emerald-300">{status.redirect_uri}</code>
          <button data-testid="copy-redirect-uri-button" onClick={copy} className="cc-btn cc-btn-ghost px-3"><Copy className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button data-testid="google-ads-connect-button" onClick={connect} disabled={!status.configured || busy} className="cc-btn cc-btn-primary">
          {busy ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <ExternalLink className="h-4 w-4" />}
          {status.has_refresh_token ? "Re-authorize Google Ads" : "Connect Google Ads"}
        </button>
        {status.has_refresh_token && (
          <button data-testid="google-ads-disconnect-button" onClick={disconnect} disabled={busy} className="cc-btn cc-btn-danger">
            <Unplug className="h-4 w-4" /> Disconnect
          </button>
        )}
      </div>
      {!status.configured && <p data-testid="connect-disabled-hint" className="mt-3 text-xs text-slate-500">Connect unlocks once Client ID, Client Secret and Login Customer ID are saved below.</p>}
    </Panel>
  );
}
